/**
 * useAvatarStateMachine — React hook that drives avatar behavior from
 * dashboard events using an XState state machine.
 *
 * Dashboard event wiring:
 *   - saik:notification          → NOTIFICATION  (alert)
 *   - saik:avatar-speaking-start → SPEAKING_START (talking)
 *   - saik:avatar-speaking-stop  → SPEAKING_STOP  (idle from talking)
 *   - saik:avatar-processing    → PROCESSING_START (thinking)
 *   - saik:avatar-success        → SUCCESS        (happy)
 *   - saik:avatar-error          → ERROR          (sad)
 *   - Any user interaction       → USER_ACTIVITY  (resets sleep timer)
 *
 * The hook returns the current AvatarState suitable for passing directly
 * to AvatarAnimator's `state` prop.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useMachine } from '@xstate/react';
import { avatarMachine, stateValueToAvatarState, type AvatarMachineEvent } from '../state/avatarMachine';
import type { AvatarState } from '../types/avatarAnimation';

// ── Dashboard event name constants ─────────────────────────────────────

const EVT_NOTIFICATION = 'saik:notification';
const EVT_NEW_MESSAGE = 'saik:avatar-new-message';
const EVT_USER_ACTIVITY = 'saik:avatar-user-activity';
const EVT_SPEAKING_START = 'saik:avatar-speaking-start';
const EVT_SPEAKING_STOP = 'saik:avatar-speaking-stop';
const EVT_PROCESSING = 'saik:avatar-processing';
const EVT_SUCCESS = 'saik:avatar-success';
const EVT_ERROR = 'saik:avatar-error';

// User-activity events that reset the sleep timer (USER_ACTIVITY event).
// These are lightweight pointer/keyboard listeners — they only fire
// when the machine is in `sleeping` state, so the overhead is minimal.
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'pointermove',
  'keydown',
  'click',
  'touchstart',
];

export interface UseAvatarStateMachineResult {
  /** Current avatar state for the animator. */
  avatarState: AvatarState;
  /** Imperatively send an event to the machine. */
  send: (event: AvatarMachineEvent) => void;
}

export function useAvatarStateMachine(): UseAvatarStateMachineResult {
  const [snapshot, send] = useMachine(avatarMachine);
  const avatarState = stateValueToAvatarState(snapshot.value as string);

  // ── Listen to dashboard CustomEvents and forward to the machine ─────
  useEffect(() => {
    const handlers: Array<[string, (e: Event) => void]> = [];

    const makeHandler = (eventType: AvatarMachineEvent['type']) =>
      () => send({ type: eventType });

    handlers.push([EVT_NOTIFICATION, makeHandler('NOTIFICATION')]);
    handlers.push([EVT_NEW_MESSAGE, makeHandler('NEW_MESSAGE')]);
    handlers.push([EVT_USER_ACTIVITY, makeHandler('USER_ACTIVITY')]);
    handlers.push([EVT_SPEAKING_START, makeHandler('SPEAKING_START')]);
    handlers.push([EVT_SPEAKING_STOP, makeHandler('SPEAKING_STOP')]);
    handlers.push([EVT_PROCESSING, makeHandler('PROCESSING_START')]);
    handlers.push([EVT_SUCCESS, makeHandler('SUCCESS')]);
    handlers.push([EVT_ERROR, makeHandler('ERROR')]);

    for (const [name, handler] of handlers) {
      window.addEventListener(name, handler as EventListener);
    }

    return () => {
      for (const [name, handler] of handlers) {
        window.removeEventListener(name, handler as EventListener);
      }
    };
  }, [send]);

  // ── User activity resets sleep timer ─────────────────────────────────
  // We send USER_ACTIVITY on pointer/keyboard events so the machine
  // exits `sleeping` (if active) and resets the idle→sleeping timer.
  const lastActivityRef = useRef(0);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    // Throttle to one event per second to avoid flooding the machine.
    if (now - lastActivityRef.current < 1000) return;
    lastActivityRef.current = now;
    send({ type: 'USER_ACTIVITY' });
  }, [send]);

  useEffect(() => {
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, handleActivity, { passive: true });
    }
    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, handleActivity);
      }
    };
  }, [handleActivity]);

  return { avatarState, send };
}